// Frontend-only translation service using Google Translate API
class TranslationService {
    // Language code mapping
    languageCodeMap = {
        English: 'en',
        Hindi: 'hi',
        Spanish: 'es',
        French: 'fr',
        Portuguese: 'pt',
        'Portuguese (Brazilian)': 'pt',
        Mandarin: 'zh-CN',
        Arabic: 'ar',
        Bengali: 'bn',
        Russian: 'ru',
        Indonesian: 'id',
        Urdu: 'ur',
        German: 'de',
        Japanese: 'ja',
        'Nigerian Pidgin': 'pcm',
        'Egyptian Arabic': 'ar',
        Turkish: 'tr',
        Cantonese: 'zh-TW',
        Tagalog: 'tl',
        Vietnamese: 'vi',
        Korean: 'ko',
        'Farsi (Persian)': 'fa',
        Romanian: 'ro',
    }

    // Get language code from language name
    getLanguageCode(languageName) {
        return this.languageCodeMap[languageName] || 'en'
    }

    // Translate multiple texts in one request using multiple q params (1:1 mapping, fewer calls)
    async translateChunk(textArr, targetLang, sourceLang) {
        const baseUrl = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=${sourceLang}&tl=${targetLang}&dt=t`
        const qParams = textArr.map(t => `q=${encodeURIComponent(t)}`).join('&')
        const url = `${baseUrl}&${qParams}`
        const response = await fetch(url)
        const result = await response.json()
        if (result && Array.isArray(result) && result[0] && Array.isArray(result[0])) {
            const translated = result[0].map(item =>
                (Array.isArray(item) && item[0] ? item[0] : '').trim() || '',
            )
            if (translated.length === textArr.length) {
                return translated
            }
        }
        const fallback = await Promise.all(
            textArr.map(text => this.translateSingle(text, targetLang, sourceLang)),
        )
        return fallback
    }

    async translateSingle(text, targetLang, sourceLang) {
        const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=${sourceLang}&tl=${targetLang}&dt=t&q=${encodeURIComponent(text)}`
        const response = await fetch(url)
        const result = await response.json()
        if (result && Array.isArray(result) && result[0] && Array.isArray(result[0])) {
            const translated = result[0]
                .map(item => (Array.isArray(item) && item[0] ? item[0] : ''))
                .join('')
                .trim()
            return translated || text
        }
        return text
    }

    // Run chunk requests recursively (avoids no-await-in-loop)
    async runChunkBatches(items, chunkSize, translateChunkFn) {
        if (items.length === 0) return []
        const chunk = items.slice(0, chunkSize)
        const rest = items.slice(chunkSize)
        const chunkResults = await translateChunkFn(chunk)
        const restResults = await this.runChunkBatches(rest, chunkSize, translateChunkFn)
        return [...chunkResults, ...restResults]
    }

    // Batch translate: ~25 texts per API call for good performance
    async translateBatch(texts, targetLanguage, sourceLanguage = 'English') {
        if (!texts || texts.length === 0 || targetLanguage === sourceLanguage) {
            return texts
        }

        try {
            const sourceLang = this.getLanguageCode(sourceLanguage)
            const targetLang = this.getLanguageCode(targetLanguage)

            const textMap = new Map()
            const textsToTranslate = []
            texts.forEach((text, index) => {
                if (text && typeof text === 'string' && text.trim()) {
                    textMap.set(index, text)
                    textsToTranslate.push(text)
                }
            })

            if (textsToTranslate.length === 0) {
                return texts
            }

            const CHUNK_SIZE = 25
            const translateChunkFn = chunk =>
                this.translateChunk(chunk, targetLang, sourceLang)
            const translatedTexts = await this.runChunkBatches(
                textsToTranslate,
                CHUNK_SIZE,
                translateChunkFn,
            )

            const translatedArray = [...texts]
            let translationIndex = 0
            textMap.forEach((originalText, originalIndex) => {
                if (translationIndex < translatedTexts.length) {
                    translatedArray[originalIndex] = translatedTexts[translationIndex]
                    translationIndex += 1
                }
            })

            return translatedArray
        } catch (error) {
            console.error('Batch translation error:', error)
            return texts
        }
    }

    // Translate template data - optimized to use batch translation
    async translateTemplate(templateData, targetLanguage, sourceLanguage = 'English') {
        if (!templateData || !targetLanguage || targetLanguage === sourceLanguage) {
            return templateData
        }

        try {
            const translatedData = JSON.parse(JSON.stringify(templateData))

            // Collect all texts that need translation
            const textsToTranslate = []
            const textReferences = []

            // Helper function to set nested property
            const setNestedProperty = (obj, path, value) => {
                let current = obj
                for (let i = 0; i < path.length - 1; i += 1) {
                    if (!current[path[i]]) {
                        current[path[i]] = {}
                    }
                    current = current[path[i]]
                }
                current[path[path.length - 1]] = value
            }

            // Collect all texts that need translation
            if (Array.isArray(translatedData.Sections)) {
                translatedData.Sections.forEach((section, sectionIndex) => {
                    if (section.SectionName) {
                        textReferences.push({
                            path: ['Sections', sectionIndex, 'SectionName'],
                            text: section.SectionName,
                        })
                        textsToTranslate.push(section.SectionName)
                    }

                    if (Array.isArray(section.Questions)) {
                        section.Questions.forEach((question, questionIndex) => {
                            // Translate Question field - strip HTML for accurate translation
                            const questionText = question.Question || question.FAQ || ''
                            if (questionText) {
                                // Remove HTML tags for translation to get clean text
                                const textContent = questionText.replace(/<[^>]*>/g, '').trim()
                                if (textContent) {
                                    // Store reference for Question field
                                    textReferences.push({
                                        path: ['Sections', sectionIndex, 'Questions', questionIndex, 'Question'],
                                        text: textContent,
                                        isHtml: questionText !== textContent,
                                        originalHtml: questionText,
                                    })
                                    textsToTranslate.push(textContent)

                                    // Also translate FAQ field (FAQ is used for display in inspection)
                                    textReferences.push({
                                        path: ['Sections', sectionIndex, 'Questions', questionIndex, 'FAQ'],
                                        text: textContent,
                                        isHtml: questionText !== textContent,
                                        originalHtml: questionText,
                                    })
                                    // Don't push to textsToTranslate again - we'll reuse the same translation
                                }
                            }

                            if (question.AdditionalInfo) {
                                textReferences.push({
                                    path: [
                                        'Sections',
                                        sectionIndex,
                                        'Questions',
                                        questionIndex,
                                        'AdditionalInfo',
                                    ],
                                    text: question.AdditionalInfo,
                                })
                                textsToTranslate.push(question.AdditionalInfo)
                            }

                            if (question.TemplateResponseType) {
                                // Multiple response options
                                if (
                                    Array.isArray(
                                        question.TemplateResponseType.MultipleResponse,
                                    )
                                ) {
                                    question.TemplateResponseType.MultipleResponse.forEach(
                                        (option, optionIndex) => {
                                            if (option.DisplayText) {
                                                textReferences.push({
                                                    path: [
                                                        'Sections',
                                                        sectionIndex,
                                                        'Questions',
                                                        questionIndex,
                                                        'TemplateResponseType',
                                                        'MultipleResponse',
                                                        optionIndex,
                                                        'DisplayText',
                                                    ],
                                                    text: option.DisplayText,
                                                })
                                                textsToTranslate.push(option.DisplayText)
                                            }
                                            if (option.HelpText) {
                                                textReferences.push({
                                                    path: [
                                                        'Sections',
                                                        sectionIndex,
                                                        'Questions',
                                                        questionIndex,
                                                        'TemplateResponseType',
                                                        'MultipleResponse',
                                                        optionIndex,
                                                        'HelpText',
                                                    ],
                                                    text: option.HelpText,
                                                })
                                                textsToTranslate.push(option.HelpText)
                                            }
                                        },
                                    )
                                }

                                // Single response options
                                if (
                                    Array.isArray(
                                        question.TemplateResponseType.SingleResponses,
                                    )
                                ) {
                                    question.TemplateResponseType.SingleResponses.forEach(
                                        (option, optionIndex) => {
                                            if (option.DisplayText) {
                                                textReferences.push({
                                                    path: [
                                                        'Sections',
                                                        sectionIndex,
                                                        'Questions',
                                                        questionIndex,
                                                        'TemplateResponseType',
                                                        'SingleResponses',
                                                        optionIndex,
                                                        'DisplayText',
                                                    ],
                                                    text: option.DisplayText,
                                                })
                                                textsToTranslate.push(option.DisplayText)
                                            }
                                        },
                                    )
                                }

                                // User multiple response options
                                if (
                                    Array.isArray(
                                        question.TemplateResponseType.UserMultipleResponse,
                                    )
                                ) {
                                    question.TemplateResponseType.UserMultipleResponse.forEach(
                                        (option, optionIndex) => {
                                            if (option.DisplayText) {
                                                textReferences.push({
                                                    path: [
                                                        'Sections',
                                                        sectionIndex,
                                                        'Questions',
                                                        questionIndex,
                                                        'TemplateResponseType',
                                                        'UserMultipleResponse',
                                                        optionIndex,
                                                        'DisplayText',
                                                    ],
                                                    text: option.DisplayText,
                                                })
                                                textsToTranslate.push(option.DisplayText)
                                            }
                                            if (option.HelpText) {
                                                textReferences.push({
                                                    path: [
                                                        'Sections',
                                                        sectionIndex,
                                                        'Questions',
                                                        questionIndex,
                                                        'TemplateResponseType',
                                                        'UserMultipleResponse',
                                                        optionIndex,
                                                        'HelpText',
                                                    ],
                                                    text: option.HelpText,
                                                })
                                                textsToTranslate.push(option.HelpText)
                                            }
                                        },
                                    )
                                }

                                // Parent-child response options (template definition)
                                if (
                                    Array.isArray(question.ParentChildResponse) &&
                                    question.ParentChildResponse.length > 0
                                ) {
                                    question.ParentChildResponse.forEach(
                                        (parentChild, parentIndex) => {
                                            if (parentChild.ParentLabel) {
                                                textReferences.push({
                                                    path: [
                                                        'Sections',
                                                        sectionIndex,
                                                        'Questions',
                                                        questionIndex,
                                                        'ParentChildResponse',
                                                        parentIndex,
                                                        'ParentLabel',
                                                    ],
                                                    text: parentChild.ParentLabel,
                                                })
                                                textsToTranslate.push(parentChild.ParentLabel)
                                            }
                                            if (parentChild.ChildLabel) {
                                                textReferences.push({
                                                    path: [
                                                        'Sections',
                                                        sectionIndex,
                                                        'Questions',
                                                        questionIndex,
                                                        'ParentChildResponse',
                                                        parentIndex,
                                                        'ChildLabel',
                                                    ],
                                                    text: parentChild.ChildLabel,
                                                })
                                                textsToTranslate.push(parentChild.ChildLabel)
                                            }

                                            if (Array.isArray(parentChild.Children)) {
                                                parentChild.Children.forEach(
                                                    (child, childIndex) => {
                                                        if (child.DisplayText) {
                                                            textReferences.push({
                                                                path: [
                                                                    'Sections',
                                                                    sectionIndex,
                                                                    'Questions',
                                                                    questionIndex,
                                                                    'ParentChildResponse',
                                                                    parentIndex,
                                                                    'Children',
                                                                    childIndex,
                                                                    'DisplayText',
                                                                ],
                                                                text: child.DisplayText,
                                                            })
                                                            textsToTranslate.push(
                                                                child.DisplayText,
                                                            )
                                                        }
                                                    },
                                                )
                                            }
                                        },
                                    )
                                }
                            }

                            // ANSWER-LEVEL TRANSLATION (used on inspection page labels and user text)
                            const answers = question.Answers

                            // Multiple-response answers (labels for selected options and dropdown)
                            if (
                                answers &&
                                Array.isArray(answers.MultipleResponse) &&
                                answers.MultipleResponse.length > 0
                            ) {
                                answers.MultipleResponse.forEach(
                                    (option, optionIndex) => {
                                        if (option.DisplayText) {
                                            textReferences.push({
                                                path: [
                                                    'Sections',
                                                    sectionIndex,
                                                    'Questions',
                                                    questionIndex,
                                                    'Answers',
                                                    'MultipleResponse',
                                                    optionIndex,
                                                    'DisplayText',
                                                ],
                                                text: option.DisplayText,
                                            })
                                            textsToTranslate.push(
                                                option.DisplayText,
                                            )
                                        }
                                        if (option.HelpText) {
                                            textReferences.push({
                                                path: [
                                                    'Sections',
                                                    sectionIndex,
                                                    'Questions',
                                                    questionIndex,
                                                    'Answers',
                                                    'MultipleResponse',
                                                    optionIndex,
                                                    'HelpText',
                                                ],
                                                text: option.HelpText,
                                            })
                                            textsToTranslate.push(
                                                option.HelpText,
                                            )
                                        }
                                    },
                                )
                            }

                            // Parent-child answers (ParentName / ChildName shown on inspection UI)
                            if (
                                answers &&
                                Array.isArray(answers.ParentChildResponse) &&
                                answers.ParentChildResponse.length > 0
                            ) {
                                answers.ParentChildResponse.forEach(
                                    (parentChild, parentIndex) => {
                                        if (parentChild.ParentName) {
                                            textReferences.push({
                                                path: [
                                                    'Sections',
                                                    sectionIndex,
                                                    'Questions',
                                                    questionIndex,
                                                    'Answers',
                                                    'ParentChildResponse',
                                                    parentIndex,
                                                    'ParentName',
                                                ],
                                                text: parentChild.ParentName,
                                            })
                                            textsToTranslate.push(
                                                parentChild.ParentName,
                                            )
                                        }
                                        if (parentChild.ChildLabel) {
                                            textReferences.push({
                                                path: [
                                                    'Sections',
                                                    sectionIndex,
                                                    'Questions',
                                                    questionIndex,
                                                    'Answers',
                                                    'ParentChildResponse',
                                                    parentIndex,
                                                    'ChildLabel',
                                                ],
                                                text: parentChild.ChildLabel,
                                            })
                                            textsToTranslate.push(
                                                parentChild.ChildLabel,
                                            )
                                        }

                                        if (
                                            Array.isArray(parentChild.Children)
                                        ) {
                                            parentChild.Children.forEach(
                                                (child, childIndex) => {
                                                    if (child.ChildName) {
                                                        textReferences.push({
                                                            path: [
                                                                'Sections',
                                                                sectionIndex,
                                                                'Questions',
                                                                questionIndex,
                                                                'Answers',
                                                                'ParentChildResponse',
                                                                parentIndex,
                                                                'Children',
                                                                childIndex,
                                                                'ChildName',
                                                            ],
                                                            text: child.ChildName,
                                                        })
                                                        textsToTranslate.push(
                                                            child.ChildName,
                                                        )
                                                    }
                                                },
                                            )
                                        }
                                    },
                                )
                            }

                            // Single-response free-text answers (e.g. TextBox / TextArea)
                            if (
                                answers &&
                                answers.SingleResponse &&
                                typeof answers.SingleResponse.Response ===
                                    'string' &&
                                answers.SingleResponse.Response.trim()
                            ) {
                                const answerText =
                                    answers.SingleResponse.Response.trim()
                                textReferences.push({
                                    path: [
                                        'Sections',
                                        sectionIndex,
                                        'Questions',
                                        questionIndex,
                                        'Answers',
                                        'SingleResponse',
                                        'Response',
                                    ],
                                    text: answerText,
                                })
                                textsToTranslate.push(answerText)
                            }

                            // Notes and Internal Notes (user-entered text on inspection page)
                            if (Array.isArray(question.Notes)) {
                                question.Notes.forEach((note, noteIndex) => {
                                    if (
                                        note &&
                                        typeof note.Notes === 'string' &&
                                        note.Notes.trim()
                                    ) {
                                        const noteText = note.Notes.trim()
                                        textReferences.push({
                                            path: [
                                                'Sections',
                                                sectionIndex,
                                                'Questions',
                                                questionIndex,
                                                'Notes',
                                                noteIndex,
                                                'Notes',
                                            ],
                                            text: noteText,
                                        })
                                        textsToTranslate.push(noteText)
                                    }
                                })
                            }
                        })
                    }
                })
            }

            // Translate all texts in one batch call
            if (textsToTranslate.length > 0) {
                const translatedTexts = await this.translateBatch(
                    textsToTranslate,
                    targetLanguage,
                    sourceLanguage,
                )

            // Apply translations back to the data structure
            // Create a map to find the correct translation index for each reference
            const translationMap = new Map()
            textsToTranslate.forEach((text, index) => {
                if (!translationMap.has(text)) {
                    translationMap.set(text, index)
                }
            })

            textReferences.forEach((ref) => {
                const translationIndex = translationMap.get(ref.text)
                if (translationIndex !== undefined && translationIndex < translatedTexts.length && translatedTexts[translationIndex]) {
                    let translatedValue = translatedTexts[translationIndex]

                    // If original had HTML, preserve the HTML structure with translated text
                    if (ref.isHtml && ref.originalHtml) {
                        // Simple HTML tag preservation - wrap translated text in same tag
                        const tagMatch = ref.originalHtml.match(/^<([^>\s]+)/i)
                        if (tagMatch) {
                            const tagName = tagMatch[1]
                            translatedValue = `<${tagName}>${translatedValue}</${tagName}>`
                        }
                    }

                    setNestedProperty(translatedData, ref.path, translatedValue)
                }
            })
            }

            return translatedData
        } catch (error) {
            console.error('Template translation error:', error)
            return templateData
        }
    }
}

export const TranslationServiceInstance = new TranslationService()
